import { Component, ChangeDetectorRef, Inject, OnInit, ViewChild } from '@angular/core';
import { WebService, lastValueFrom, AuthService, RmsPulldownService, RmsMessageService } from '@services';
import { RmsModule } from '@rms';
import { SwapDataService, SwapDecision, SwapTypeID, downloadCsv } from '../../../../../services/swap-data.service';
import { RmsPulldownRef } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RMS_PULLDOWN_DATA } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';
import { RmsTabGroupComponent } from '../../../../../custom-modules/rms-module/rms-tab-group/rms-tab-group.component';
import { OrderFormComponent } from '../../order-management/order-form/order-form.component';

// =====================================================================
// SwapDetailComponent — Scope 6.2 pulldown (new + existing swaps)
// ---------------------------------------------------------------------
// Swap types (dbo.swap_type):
//   1 Reserve → Reserve   bags move into the requesting customer's reserve
//   2 Reserve → Order     bags move onto a new Draft order
//   3 Release             bags go back to general population (no receiver)
//
// Tabs:
//   0  Request & Source   type, request fields, find/pick source          (6.2.2–6.2.17)
//   1  Approval           trader decision + approval history              (6.2.18–6.2.23, 6.2.36–38)
//   2  Complete Swap      before/after preview, validations, execute      (6.2.24–6.2.35, 6.2.41–45)
//   3  Notes              add/edit notes                                   (6.2.46)
//   4  Audit              immutable history + CSV export                   (6.2.39, 6.2.47–48)
//
// Status IDs (dbo.swap_status):
//   1 Draft · 2 Pending Approval · 3 Returned · 4 Approved
//   5 Rejected · 6 Completed · 7 Cancelled
// =====================================================================

// Same 3 warehouses 5.1 order creation is restricted to (manager directive).
// 43 (RNY Annex) is treated as part of 37 by the procs.
const ALLOWED_WAREHOUSE_IDS = [37, 45, 31];

// Same AD groups that can override zero-price in 5.2 — they can also
// decide any swap. The proc enforces this too; this only drives the UI.
const SWAP_ADMIN_GROUPS = ['Executive', 'System Admins'];

const TAB = { Request: 0, Approval: 1, Complete: 2, Notes: 3, Audit: 4 };

@Component({
  selector: 'app-swap-detail',
  imports: [RmsModule],
  templateUrl: './swap-detail.component.html',
  styleUrl: './swap-detail.component.scss'
})
export class SwapDetailComponent implements OnInit {
  @ViewChild(RmsTabGroupComponent) tabGroup?: RmsTabGroupComponent;

  SwapRequestID: number | null = null;

  // Loaded = first load finished (tabs render once and stay mounted so the
  // active tab doesn't reset). Busy = any action in flight.
  Loaded = false;
  Busy = false;

  Swap: any = null;
  History: any[] = [];
  Notes: any[] = [];
  Preview: any = null;

  // ---- Swap type ----
  SwapTypeID: SwapTypeID = 2;
  SwapTypes: { id: SwapTypeID; label: string; hint: string }[] = [
    { id: 2, label: 'Reserve → Order', hint: 'Bags move from another customer\'s reserve onto a new Draft order.' },
    { id: 1, label: 'Reserve → Reserve', hint: 'Bags move from another customer\'s reserve into this customer\'s reserve.' },
    { id: 3, label: 'Release', hint: 'Bags leave a customer\'s reserve and go back to general population.' }
  ];

  // ---- Lookups ----
  Customers: any[] = [];
  CoffeeOptions: any[] = [];
  Warehouses: any[] = [];
  PriorityOptions = ['Low', 'Normal', 'High', 'Urgent'];

  // ---- Request form, types 1/2 (6.2.2, 6.2.4, 6.2.5) ----
  RequestingCompanyID: number | null = null;
  RequestedInventoryTypeID: number | null = null;
  BagsRequested: number | null = null;
  DestinationWarehouseID: number | null = 37;
  CrossWarehouseAllowed = false;
  TargetDate: string | null = null;
  Priority = 'Normal';
  Reason = '';

  // ---- Release (type 3): whose reserve, optional coffee filter ----
  ReleaseCompanyID: number | null = null;
  ReleaseCoffeeFilter: number | null = null;
  ReserveLines: any[] = [];
  LoadingLines = false;
  LinesLoaded = false;

  // ---- Candidate search, types 1/2 (6.2.3, 6.2.6–6.2.16) ----
  IncludeAlternatives = true;
  Searching = false;
  Searched = false;
  Candidates: any[] = [];
  CandidateSummary: any = null;

  // ---- Chosen source (6.2.17) ----
  SelectedSource: any = null;
  ProposedBags: number | null = null;

  // ---- Approval (6.2.19–6.2.22) ----
  DecisionComments = '';
  RevisedBags: number | null = null;
  ApprovedPrice: number | null = null;

  // ---- Notes form (6.2.46) ----
  NoteTypes = ['General', 'Inventory', 'Source Selection', 'Analysis', 'Approval', 'Execution'];
  RelatedSteps = ['Request', 'Find Source', 'Approval', 'Complete Swap'];
  NoteForm = this.emptyNote();

  constructor(
    private swapData: SwapDataService,
    private webService: WebService,
    private auth: AuthService,
    private pulldownService: RmsPulldownService,
    private msg: RmsMessageService,
    private pulldownRef: RmsPulldownRef,
    private cdr: ChangeDetectorRef,
    @Inject(RMS_PULLDOWN_DATA) private pulldownData: { SwapRequestID?: number | null }
  ) { }

  // =====================================================================
  // Status / type helpers
  // =====================================================================

  get statusId(): number { return this.Swap?.SwapStatusID ?? 1; }
  get isNew(): boolean { return !this.SwapRequestID; }
  get isRelease(): boolean { return this.SwapTypeID === 3; }

  get typeLabel(): string { return this.SwapTypes.find(t => t.id === this.SwapTypeID)?.label ?? ''; }
  get typeHint(): string { return this.SwapTypes.find(t => t.id === this.SwapTypeID)?.hint ?? ''; }

  /** Request + source can only change while Draft or Returned for Changes */
  get canEdit(): boolean { return this.isNew || this.statusId === 1 || this.statusId === 3; }

  get canSubmit(): boolean {
    return this.canEdit && !!this.SelectedSource && (this.ProposedBags ?? 0) > 0;
  }

  get isAdmin(): boolean { return this.auth.isInAnyGroup(SWAP_ADMIN_GROUPS); }

  /** Approving trader, an admin, or anyone if that trader has no login linked */
  get canDecide(): boolean {
    if (this.statusId !== 2) return false;
    const approver = this.Swap?.ApprovingTraderGUID;
    const me = this.swapData.currentUserGUID;
    if (!approver) return true;
    return this.isAdmin || (!!me && approver.toLowerCase() === me.toLowerCase());
  }

  get canExecute(): boolean { return this.statusId === 4 && this.Preview?.ReadyToExecute === 1; }

  get canCancel(): boolean { return !this.isNew && [1, 2, 3, 4].includes(this.statusId); }

  get approvalHistory(): any[] { return this.History.filter(h => h.EntityType === 'Approval'); }

  /** Execute button text per type */
  get executeLabel(): string {
    switch (this.SwapTypeID) {
      case 1: return 'Execute Swap & Move to Reserve';
      case 3: return 'Execute Release';
      default: return 'Execute Swap & Create Draft Order';
    }
  }

  /** Middle before/after card title per type */
  get receivingCardTitle(): string {
    switch (this.SwapTypeID) {
      case 1: return 'Receiving reserve';
      case 3: return 'Released to stock';
      default: return 'New Draft order';
    }
  }

  // =====================================================================
  // Load
  // =====================================================================

  async ngOnInit() {
    this.SwapRequestID = this.pulldownData?.SwapRequestID ?? null;

    try {
      await this.loadLookups();
      if (this.SwapRequestID) {
        await this.loadSwap();
      }
    } catch (err: any) {
      this.msg.error(this.errMsg(err, 'Could not load swap.'));
    } finally {
      this.Loaded = true;
      this.cdr.markForCheck();
    }
  }

  private async loadLookups() {
    const [customers, coffees, locations] = await Promise.all([
      lastValueFrom(this.webService.getWebCall('customers', 'coffee/customers', {
        params: [{ name: 'IncludeInactive', value: 0 }]
      })),
      lastValueFrom(this.swapData.getCoffeeOptions()),
      lastValueFrom(this.webService.getWebCall('data', 'coffee/locations'))
    ]);

    this.Customers = customers ?? [];

    // Label shows open reserve bags so traders can see where swaps are possible
    this.CoffeeOptions = (coffees ?? []).map((c: any) => ({
      ...c,
      LabelName: c.OpenReserveBags > 0
        ? `${c.InventoryTypeName}  —  ${c.OpenReserveBags} open reserve bags (${c.HoldingCustomers} customers)`
        : c.InventoryTypeName
    }));

    this.Warehouses = (locations ?? []).filter((w: any) => ALLOWED_WAREHOUSE_IDS.includes(w.LocationID));
  }

  /** Customers not in the picker list (get_customers filters some out) are added so their name still shows */
  private ensureCustomerListed(companyId: number | null, name: string | null) {
    if (companyId && !this.Customers.some(c => c.CompanyID === companyId)) {
      this.Customers = [...this.Customers, { CompanyID: companyId, CompanyName: name, LabelName: name }];
    }
  }

  /** Reloads swap, history, notes (+ preview when a source is set) */
  async loadSwap() {
    if (!this.SwapRequestID) return;

    const detail = await lastValueFrom(this.swapData.getRequestDetail(this.SwapRequestID));
    this.Swap = detail?.Swap ?? null;
    this.History = detail?.History ?? [];
    this.Notes = detail?.Notes ?? [];

    if (!this.Swap) return;

    this.SwapTypeID = (this.Swap.SwapTypeID ?? 2) as SwapTypeID;

    // Request fields
    this.RequestingCompanyID = this.Swap.RequestingCompanyID;
    this.ensureCustomerListed(this.RequestingCompanyID, this.Swap.RequestingCustomer);

    this.RequestedInventoryTypeID = this.Swap.RequestedInventoryTypeID;
    this.BagsRequested = this.Swap.BagsRequested;
    this.DestinationWarehouseID = this.Swap.DestinationWarehouseID;
    this.CrossWarehouseAllowed = !!this.Swap.CrossWarehouseAllowed;
    this.TargetDate = this.Swap.TargetDate ? String(this.Swap.TargetDate).substring(0, 10) : null;
    this.Priority = this.Swap.Priority ?? 'Normal';
    this.Reason = this.Swap.Reason ?? '';

    // Release: the "whose reserve" picker is the source customer
    if (this.isRelease) {
      this.ReleaseCompanyID = this.Swap.SourceCompanyID;
      this.ensureCustomerListed(this.ReleaseCompanyID, this.Swap.SourceCustomer);
    }

    // Saved source → same shape as a candidate row so the template can reuse it
    this.SelectedSource = this.Swap.SourceReserveOrderID ? {
      ReserveOrderID: this.Swap.SourceReserveOrderID,
      InventoryID: this.Swap.SourceInventoryID,
      SourceCustomer: this.Swap.SourceCustomer,
      InventoryTypeName: this.Swap.SourceCoffee,
      WarehouseName: this.Swap.SourceWarehouse,
      AvailableToSwap: (this.Swap.SourceCurrentBags ?? 0) - (this.Swap.OtherHeldBags ?? 0),
      Price: this.Swap.SourcePrice,
      PriceStatus: this.Swap.SourcePriceStatus,
      MatchType: this.Swap.MatchType,
      MatchScore: this.Swap.MatchScore,
      ApprovingTraderName: this.Swap.ApprovingTraderName
    } : null;
    this.ProposedBags = this.Swap.ProposedBags;

    // Approval form defaults
    this.RevisedBags = this.Swap.ApprovedBags ?? this.Swap.ProposedBags;
    this.ApprovedPrice = null;
    this.DecisionComments = '';

    // Preview only matters once a source exists and the swap is still open
    this.Preview = (this.Swap.SourceReserveOrderID && [1, 2, 3, 4].includes(this.statusId))
      ? await lastValueFrom(this.swapData.getExecutionPreview(this.SwapRequestID))
      : null;

    this.cdr.markForCheck();
  }

  // =====================================================================
  // Request & Source tab
  // =====================================================================

  /** Switching type starts the source selection over */
  onTypeChanged(typeId: SwapTypeID) {
    this.SwapTypeID = typeId;
    this.onRequestKeyChanged();
    this.ReserveLines = [];
    this.LinesLoaded = false;
  }

  /** Changing customer or coffee invalidates any results / chosen source */
  onRequestKeyChanged() {
    this.Candidates = [];
    this.CandidateSummary = null;
    this.Searched = false;
    this.SelectedSource = null;
    this.ProposedBags = null;
  }

  private validateRequest(): string | null {
    // Release has one quantity — "Bags to release" on the selected line
    if (this.isRelease) {
      if (!this.ReleaseCompanyID) return 'Select whose reserve the bags come from.';
      if (!this.SelectedSource) return 'Select the reserve line to release.';
      if (!this.ProposedBags || this.ProposedBags <= 0) return 'Enter how many bags to release.';
      if (this.ProposedBags > this.SelectedSource.AvailableToSwap) {
        return `Only ${this.SelectedSource.AvailableToSwap} bags are available on that line.`;
      }
      return null;
    }
    if (!this.BagsRequested || this.BagsRequested <= 0) return 'Enter how many bags are needed.';
    if (!this.RequestingCompanyID) return 'Select the requesting customer.';
    if (!this.RequestedInventoryTypeID) return 'Select the coffee the customer needs.';
    return null;
  }

  // ---- Types 1/2: ranked search across other customers' reserves ----
  async findSources() {
    const problem = this.validateRequest();
    if (problem) { this.msg.warn(problem); return; }

    this.Searching = true;
    try {
      const result = await lastValueFrom(this.swapData.getCandidates({
        RequestingCompanyID: this.RequestingCompanyID!,
        InventoryTypeID: this.RequestedInventoryTypeID!,
        BagsNeeded: this.BagsRequested!,
        WarehouseID: this.DestinationWarehouseID,
        CrossWarehouseAllowed: this.CrossWarehouseAllowed,
        IncludeAlternatives: this.IncludeAlternatives,
        ExcludeSwapRequestID: this.SwapRequestID
      }));
      this.Candidates = result?.Candidates ?? [];
      this.CandidateSummary = result?.Summary ?? null;
      this.Searched = true;
    } catch (err: any) {
      this.msg.error(this.errMsg(err, 'Candidate search failed.'));
    } finally {
      this.Searching = false;
      this.cdr.markForCheck();
    }
  }

  // ---- Type 3: list the chosen customer's own reserve lines ----
  async loadReserveLines() {
    if (!this.ReleaseCompanyID) { this.msg.warn('Select whose reserve the bags come from.'); return; }

    this.LoadingLines = true;
    try {
      this.ReserveLines = await lastValueFrom(this.swapData.getReserveLines(
        this.ReleaseCompanyID, this.ReleaseCoffeeFilter, this.SwapRequestID
      )) ?? [];
      this.LinesLoaded = true;
    } catch (err: any) {
      this.msg.error(this.errMsg(err, 'Could not load reserve lines.'));
    } finally {
      this.LoadingLines = false;
      this.cdr.markForCheck();
    }
  }

  onReleaseCustomerChanged(companyId: number | null) {
    this.ReleaseCompanyID = companyId;
    this.onRequestKeyChanged();
    this.ReserveLines = [];
    this.LinesLoaded = false;
  }

  selectCandidate(c: any) {
    this.SelectedSource = c;
    // Types 1/2 come with a recommendation; Release starts empty so the trader types the qty
    this.ProposedBags = c.RecommendedTransferBags ?? null;
  }

  isSelected(c: any): boolean {
    return !!this.SelectedSource
      && this.SelectedSource.ReserveOrderID === c.ReserveOrderID
      && this.SelectedSource.InventoryID === c.InventoryID;
  }

  clearSource() {
    this.SelectedSource = null;
    this.ProposedBags = null;
  }

  /** Saves the request (+ source). Returns true on success. */
  async saveDraft(showMessage = true): Promise<boolean> {
    const problem = this.validateRequest();
    if (problem) { this.msg.warn(problem); return false; }

    this.Busy = true;
    try {
      const result = await lastValueFrom(this.swapData.saveRequest({
        SwapRequestID: this.SwapRequestID,
        SwapTypeID: this.SwapTypeID,
        RequestingCompanyID: this.isRelease ? null : this.RequestingCompanyID,
        RequestedInventoryTypeID: this.isRelease ? null : this.RequestedInventoryTypeID,
        BagsRequested: this.isRelease ? this.ProposedBags! : this.BagsRequested!,
        DestinationWarehouseID: this.isRelease ? null : this.DestinationWarehouseID,
        CrossWarehouseAllowed: this.isRelease ? false : this.CrossWarehouseAllowed,
        TargetDate: this.TargetDate,
        Priority: this.Priority,
        Reason: this.Reason || null,
        SourceReserveOrderID: this.SelectedSource?.ReserveOrderID ?? null,
        SourceInventoryID: this.SelectedSource?.InventoryID ?? null,
        MatchType: this.SelectedSource?.MatchType ?? null,
        MatchScore: this.SelectedSource?.MatchScore ?? null,
        ProposedBags: this.SelectedSource ? this.ProposedBags : null
      }));

      this.SwapRequestID = result.SwapRequestID;
      await this.loadSwap();
      if (showMessage) this.msg.success(`${this.Swap?.SwapNumber ?? 'Swap'} saved.`);
      return true;
    } catch (err: any) {
      this.msg.error(this.errMsg(err, 'Save failed.'));
      return false;
    } finally {
      this.Busy = false;
      this.cdr.markForCheck();
    }
  }

  /** Save first (so edits aren't lost), then send to the approving trader */
  async submitForApproval() {
    if (!this.canSubmit) { this.msg.warn('Select a source reserve and quantity first.'); return; }

    const saved = await this.saveDraft(false);
    if (!saved || !this.SwapRequestID) return;

    this.Busy = true;
    try {
      const result = await lastValueFrom(this.swapData.submitRequest(this.SwapRequestID));
      await this.loadSwap();
      this.msg.success(`Submitted to ${result?.ApproverName ?? 'the approving trader'} for approval.`);
      this.goToTab(TAB.Approval);
    } catch (err: any) {
      this.msg.error(this.errMsg(err, 'Submit failed.'));
    } finally {
      this.Busy = false;
      this.cdr.markForCheck();
    }
  }

  // =====================================================================
  // Approval tab
  // =====================================================================

  async decide(decision: SwapDecision) {
    if (!this.SwapRequestID) return;

    if (!this.DecisionComments.trim()) {
      this.msg.warn('Approval comments are required.');
      return;
    }
    if (decision === 'ApproveRevised' && (!this.RevisedBags || this.RevisedBags <= 0)) {
      this.msg.warn('Enter the revised quantity to approve.');
      return;
    }
    // 6.2.38 — pricing can go up, never down (the proc blocks this too)
    if (this.ApprovedPrice !== null && this.Swap?.SourcePrice !== null && this.ApprovedPrice < this.Swap.SourcePrice) {
      this.msg.warn(`Price cannot be lower than the reserve price (${this.Swap.SourcePrice}).`);
      return;
    }

    this.Busy = true;
    try {
      const approving = decision === 'Approve' || decision === 'ApproveRevised';
      const result = await lastValueFrom(this.swapData.decideRequest(
        this.SwapRequestID,
        decision,
        this.DecisionComments.trim(),
        decision === 'ApproveRevised' ? this.RevisedBags : null,
        approving ? this.ApprovedPrice : null
      ));
      await this.loadSwap();
      this.msg.success(`${result?.SwapNumber}: ${result?.StatusName}`);
      if (this.statusId === 4) this.goToTab(TAB.Complete);
    } catch (err: any) {
      this.msg.error(this.errMsg(err, 'Decision failed.'));
    } finally {
      this.Busy = false;
      this.cdr.markForCheck();
    }
  }

  // =====================================================================
  // Complete Swap tab
  // =====================================================================

  async refreshPreview() {
    if (!this.SwapRequestID) return;
    try {
      this.Preview = await lastValueFrom(this.swapData.getExecutionPreview(this.SwapRequestID));
    } catch (err: any) {
      this.msg.error(this.errMsg(err, 'Could not load preview.'));
    }
    this.cdr.markForCheck();
  }

  async executeSwap() {
    if (!this.SwapRequestID || !this.canExecute) return;

    const p = this.Preview;
    const what = this.SwapTypeID === 3
      ? `release ${p?.BagsToOrder} bags from ${p?.SourceCustomer}'s reserve ${p?.SourceReserveOrderID} back to general population`
      : `move ${p?.BagsToOrder} bags from ${p?.SourceCustomer}'s reserve ${p?.SourceReserveOrderID}.\n→ ${p?.TargetDescription}`;

    const ok = window.confirm(`Execute ${this.Swap?.SwapNumber}?\n\nThis will ${what}.\n\nThis cannot be undone.`);
    if (!ok) return;

    this.Busy = true;
    try {
      const result = await lastValueFrom(this.swapData.executeSwap(this.SwapRequestID));
      await this.loadSwap();

      if (result.SwapTypeID === 1) {
        this.msg.success(result.TargetReserveCreated
          ? `Swap completed — new reserve ${result.TargetReserveOrderID} created.`
          : `Swap completed — bags added to reserve ${result.TargetReserveOrderID}.`);
      } else if (result.SwapTypeID === 3) {
        this.msg.success('Release completed — bags are back in general population.');
      } else {
        this.msg.success(`Swap completed — Draft order ${result.OrderID} created.`);
      }
    } catch (err: any) {
      this.msg.error(this.errMsg(err, 'Execute failed.'));
      await this.refreshPreview();   // show whichever validation now fails
    } finally {
      this.Busy = false;
      this.cdr.markForCheck();
    }
  }

  /** Opens the swap's Draft order in the 5.1 order form to finish pickup, trucker, payment */
  openDraftOrder() {
    const orderId = this.Swap?.CreatedOrderID;
    if (!orderId) return;

    this.pulldownService.open(OrderFormComponent, {
      data: { OrderID: orderId },
      title: `Edit Order #${orderId}`,
      width: '85vw',
      maxWidth: '95vw',
      top: '0',
      maxHeight: '92vh',
      minHeight: '70vh'
    });
  }

  // =====================================================================
  // Cancel
  // =====================================================================

  async cancelSwap() {
    if (!this.SwapRequestID || !this.canCancel) return;

    const reason = window.prompt('Reason for cancelling this swap:');
    if (reason === null) return;   // pressed Cancel on the prompt

    this.Busy = true;
    try {
      await lastValueFrom(this.swapData.cancelRequest(this.SwapRequestID, reason || null));
      await this.loadSwap();
      this.msg.success(`${this.Swap?.SwapNumber} cancelled.`);
    } catch (err: any) {
      this.msg.error(this.errMsg(err, 'Cancel failed.'));
    } finally {
      this.Busy = false;
      this.cdr.markForCheck();
    }
  }

  // =====================================================================
  // Notes tab
  // =====================================================================

  emptyNote() {
    return {
      SwapNoteID: null as number | null,
      NoteType: 'General',
      IsInternal: true,
      RelatedStep: null as string | null,
      FollowUpRequired: false,
      FollowUpDate: null as string | null,
      NoteText: ''
    };
  }

  editNote(n: any) {
    this.NoteForm = {
      SwapNoteID: n.SwapNoteID,
      NoteType: n.NoteType,
      IsInternal: !!n.IsInternal,
      RelatedStep: n.RelatedStep,
      FollowUpRequired: !!n.FollowUpRequired,
      FollowUpDate: n.FollowUpDate ? String(n.FollowUpDate).substring(0, 10) : null,
      NoteText: n.NoteText
    };
  }

  resetNote() {
    this.NoteForm = this.emptyNote();
  }

  async saveNote() {
    if (!this.SwapRequestID) return;
    if (!this.NoteForm.NoteText.trim()) { this.msg.warn('Note text is required.'); return; }

    this.Busy = true;
    try {
      await lastValueFrom(this.swapData.saveNote({
        ...this.NoteForm,
        SwapRequestID: this.SwapRequestID,
        NoteText: this.NoteForm.NoteText.trim(),
        FollowUpDate: this.NoteForm.FollowUpRequired ? this.NoteForm.FollowUpDate : null
      }));
      this.resetNote();
      await this.loadSwap();
      this.msg.success('Note saved.');
    } catch (err: any) {
      this.msg.error(this.errMsg(err, 'Could not save note.'));
    } finally {
      this.Busy = false;
      this.cdr.markForCheck();
    }
  }

  // =====================================================================
  // Audit tab
  // =====================================================================

  async exportAudit() {
    if (!this.SwapRequestID) return;
    try {
      const rows = await lastValueFrom(this.swapData.getAuditReport({ SwapRequestID: this.SwapRequestID }));
      downloadCsv(rows ?? [], `${this.Swap?.SwapNumber ?? 'swap'}-audit.csv`);
    } catch (err: any) {
      this.msg.error(this.errMsg(err, 'Export failed.'));
    }
  }

  // =====================================================================
  // Misc
  // =====================================================================

  bandClass(score: number | null): string {
    if (score === null || score === undefined) return 'band-weak';
    if (score >= 90) return 'band-excellent';
    if (score >= 70) return 'band-good';
    if (score >= 50) return 'band-fair';
    return 'band-weak';
  }

  statusClass(): string {
    return `status-${this.statusId}`;
  }

  goToTab(index: number) {
    this.tabGroup?.selectTab(index);
    this.cdr.markForCheck();
  }

  returnToList() {
    this.pulldownRef.close(this.SwapRequestID);
  }

  /** Business-rule errors come back from the API as 400 { error } */
  private errMsg(err: any, fallback: string): string {
    return err?.error?.error ?? err?.message ?? fallback;
  }
}
